import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Grid2X2, LayoutGrid } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState(["all"]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const sort = params.get("sort") || "default";
  const [view, setView] = useState(() => localStorage.getItem("goshi.shop.view") || "spacious");

  const changeView = (v) => { setView(v); localStorage.setItem("goshi.shop.view", v); };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(["all", ...data.map(c => c.name)])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: category !== "all" ? { category } : {} })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category]);

  const sorted = useMemo(() => {
    const list = [...products];
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, sort]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <div className="pt-32 pb-24 px-6 md:px-12">
      <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Collection</p>
          <h1 className="font-serif text-5xl md:text-6xl">{category === "all" ? "All Pieces" : category}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center bg-[#141414] border border-[rgba(244,244,240,0.06)]" data-testid="shop-view-toggle">
            <button
              onClick={() => changeView("spacious")}
              className={`p-2.5 ${view === "spacious" ? "text-gold bg-gold/10" : "text-muted hover:text-gold"}`}
              aria-label="Spacious grid"
              data-testid="view-spacious"
            ><LayoutGrid size={13} strokeWidth={1.5} /></button>
            <button
              onClick={() => changeView("dense")}
              className={`p-2.5 ${view === "dense" ? "text-gold bg-gold/10" : "text-muted hover:text-gold"}`}
              aria-label="Compact grid"
              data-testid="view-dense"
            ><Grid2X2 size={13} strokeWidth={1.5} /></button>
          </div>
          <select
            value={sort}
            onChange={(e) => setFilter("sort", e.target.value === "default" ? "" : e.target.value)}
            className="bg-transparent border border-[rgba(244,244,240,0.1)] px-4 py-3 text-xs uppercase tracking-[0.2em] text-muted focus:border-[#C8A97E] outline-none"
            data-testid="sort-select"
          >
            <option value="default" className="bg-[#0C0C0C]">Featured</option>
            <option value="price-asc" className="bg-[#0C0C0C]">Price: Low → High</option>
            <option value="price-desc" className="bg-[#0C0C0C]">Price: High → Low</option>
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-12 border-b border-[rgba(244,244,240,0.06)] pb-6">
        {cats.map(c => (
          <button
            key={c}
            onClick={() => setFilter("category", c === "all" ? "" : c)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${category === c ? "text-gold border-b border-[#C8A97E]" : "text-muted hover:text-gold"}`}
            data-testid={`filter-${c}`}
          >
            {c === "all" ? "All" : c}
          </button>
        ))}
      </div>

      {(() => {
        if (loading) {
          return <p className="text-muted text-center py-24 uppercase tracking-[0.2em] text-xs">Loading the edit…</p>;
        }
        if (sorted.length === 0) {
          return <p className="text-muted text-center py-24">No pieces in this edit yet.</p>;
        }
        return (
          <div className={view === "dense"
            ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4"
            : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-12"}>
            {sorted.map(p => <ProductCard key={p.product_id} product={p} />)}
          </div>
        );
      })()}
    </div>
  );
}
